import { CustomRouteSegment } from '../../types/CustomRoute';
import { LatLng } from '../../types/Geo';
import { getEndHandleIndex } from './handles';

export const isLinkedToNextSegment = (segments: CustomRouteSegment[], segmentIndex?: number) => {
  if (segmentIndex === undefined || segmentIndex === segments.length - 1) {
    return false;
  }

  const currentSegment = segments[segmentIndex];
  const nextSegment = segments[segmentIndex + 1];
  if (!currentSegment || !nextSegment) {
    return false;
  }

  return currentSegment.isLinkedToNextSegment;
};

export const isLinkedToPreviousSegment = (segments: CustomRouteSegment[], segmentIndex?: number) => {
  if (segmentIndex === undefined || segmentIndex === 0) {
    return false;
  }

  const previousSegment = segments[segmentIndex - 1];
  if (!previousSegment) {
    return false;
  }

  return previousSegment.isLinkedToNextSegment;
};

export const canLinkSegments = (previousSegment?: CustomRouteSegment, nextSegment?: CustomRouteSegment) => {
  if (!previousSegment || !nextSegment) {
    return false;
  }

  if (previousSegment.isLinkedToNextSegment) {
    return false;
  }

  if (previousSegment.paths.length === 0 || nextSegment.paths.length === 0) {
    return false;
  }

  return true;
};

const canLinkSegmentToNextSegment = (segments: CustomRouteSegment[], segmentIndex?: number) => {
  if (segmentIndex === undefined || segmentIndex === segments.length - 1) {
    return false;
  }

  const previousSegment = segments[segmentIndex];
  const nextSegment = segments[segmentIndex + 1];

  return canLinkSegments(previousSegment, nextSegment);
};

const canLinkSegmentToPreviousSegment = (segments: CustomRouteSegment[], segmentIndex?: number) => {
  if (segmentIndex === undefined || segmentIndex === 0) {
    return false;
  }

  const previousSegment = segments[segmentIndex - 1];
  const nextSegment = segments[segmentIndex];

  return canLinkSegments(previousSegment, nextSegment);
};

export const getPointsToLinkSegmentToNextSegment = (segments: CustomRouteSegment[], segmentIndex?: number) => {
  if (!canLinkSegmentToNextSegment(segments, segmentIndex)) {
    return undefined;
  }

  const segment = segments[segmentIndex!];
  const endPath = segment.paths[segment.paths.length - 1];
  const endPoint = endPath.points[endPath.points.length - 1];
  const nextSegment = segments[segmentIndex! + 1];
  const startPoint = nextSegment.paths[0].points[0];
  return [endPoint, startPoint];
};

export const getPointsToLinkSegmentToPreviousSegment = (segments: CustomRouteSegment[], segmentIndex?: number) => {
  if (!canLinkSegmentToPreviousSegment(segments, segmentIndex)) {
    return undefined;
  }

  const segment = segments[segmentIndex!];
  const previousSegment = segments[segmentIndex! - 1];
  const endPath = previousSegment.paths[previousSegment.paths.length - 1];
  const endPoint = endPath.points[endPath.points.length - 1];
  const startPoint = segment.paths[0].points[0];
  return [endPoint, startPoint];
};

/**
 * The neighboring endpoint a segment's start or end can link to: the previous segment's end or the next segment's start.
 * @param segmentIndex may be one past the last segment, for a segment not added yet
 */
export const getAdjacentEndpointToLink = (segments: CustomRouteSegment[], segmentIndex: number, end: 'start' | 'end'): LatLng | undefined => {
  const isStart = end === 'start';
  const neighbor = segments[isStart ? segmentIndex - 1 : segmentIndex + 1];
  const isLinked = isStart ? neighbor?.isLinkedToNextSegment : segments[segmentIndex]?.isLinkedToNextSegment;
  if (!neighbor?.paths.length || isLinked) {
    return undefined;
  }

  const path = isStart ? neighbor.paths[neighbor.paths.length - 1] : neighbor.paths[0];
  return isStart ? path.points[path.points.length - 1] : path.points[0];
};

/**
 * The handles of linked neighbors that sit on a segment's handle and so move with it: the next segment's start under its end
 * handle, and the previous segment's end under its start handle. A single point segment's only handle is both.
 */
export const getLinkedHandles = (segments: CustomRouteSegment[], segmentIndex: number, handleIndex: number) => {
  const segment = segments[segmentIndex];
  if (!segment) {
    return [];
  }
  const linkedHandles: { segmentIndex: number; handleIndex: number }[] = [];
  if (handleIndex === 0 && isLinkedToPreviousSegment(segments, segmentIndex)) {
    linkedHandles.push({ segmentIndex: segmentIndex - 1, handleIndex: getEndHandleIndex(segments[segmentIndex - 1]) });
  }
  if (handleIndex === getEndHandleIndex(segment) && isLinkedToNextSegment(segments, segmentIndex)) {
    linkedHandles.push({ segmentIndex: segmentIndex + 1, handleIndex: 0 });
  }
  return linkedHandles;
};
